import * as React from 'react';
import {createUserIdentity, fetchUser, UserIdentity} from './usermanagement-lib';

import debounce from 'lodash/debounce';
import {Button, Dimmer, Form, Grid, Header, Icon, Input, Loader, Message} from 'semantic-ui-react';
import _get from 'lodash/get';
import { RouteComponentProps, withRouter } from 'react-router';
import ErrorBox from '../../components/ErrorBox/ErrorBox';
import {AnalyticsTracking} from "../../components/AnalyticsTracking/AnalyticsTracking";

interface NewUserProps extends RouteComponentProps<{}> {
}

interface NewUserState {
  editIdentity?: Partial<UserIdentity>;
  saving?: boolean;
  error?: string;

  userMailState?: 'none' | 'checking' | 'free' | 'exists';
}

class NewUser extends React.Component<NewUserProps, NewUserState> {
  constructor(props: NewUserProps) {
    super(props);

    this.state = {
      editIdentity: {
        username: '',
        email: ''
      },
      saving: false,
      userMailState: 'none'
    };

    this.checkUserAlreadyExists = debounce(this.checkUserAlreadyExists.bind(this), 750);
  }

  checkUserAlreadyExists() {
    if(!this.state.editIdentity || !this.state.editIdentity.username) {
      this.setState({
        userMailState: 'none'
      });
      return;
    }
    if(this.state.editIdentity.username.trim() === '' || this.state.editIdentity.username.trim().length < 3
        || this.state.editIdentity.username.indexOf('@') === -1) {
      this.setState({
        userMailState: 'none'
      });
      return;
    }
    this.setState({
      userMailState: 'checking'
    }, () => {
      if(!this.state.editIdentity || !this.state.editIdentity.username) {
        this.setState({userMailState: 'none'});
        return;
      }
      fetchUser(this.state.editIdentity.username)
          .then(_ => {
            // 200 , user exists
            this.setState({userMailState: 'exists'});
          }).catch(err => {
            if(err.response && err.response.status === 404) {
              // 404, user does not exist, this is good news
              this.setState({userMailState: 'free'});
              ErrorBox.reset(); // override default 404 handling...
              return;
            }
            // other error
            this.setState({userMailState: 'none'});
          });
    })
  }

  cancel() {
    this.props.history.push('/settings/users');
  }

  navigateToCreatedUser() {
    if(!this.state.editIdentity) {
      return;
    }
    const userName = this.state.editIdentity.username;
    this.props.history.push(`/settings/users/${userName}`);
  }

  saveIdentity() {
    if(!this.state.editIdentity) {
      return;
    }
    this.setState({
      saving: true,
      error: ''
    }, () => {
      createUserIdentity(this.state.editIdentity || {})
      .then(() => {
        this.setState({
          saving: true  // keep loading state until navigated away from page
        }, () => this.navigateToCreatedUser());
      }).catch((error: any) => {
        let errorMessage = 'Benutzer konnte nicht angelegt werden.';
        if(error.response && error.response.data && error.response.data.error) {
          if(error.response.data.error === 'user.already.exists') {
            errorMessage = 'Es existiert bereits ein Benutzer mit dieser E-Mail-Adresse';
          }
        }
        this.setState({
          saving: false,
          error: errorMessage
        });
      });
    })
  }

  canSave() {
    return this.state.editIdentity &&
            this.state.userMailState === 'free' &&
            this.state.editIdentity.email &&
            this.state.editIdentity.email.indexOf('@') !== -1 &&
        ((this.state.editIdentity.lastName && this.state.editIdentity.lastName.trim().length > 1)
        || (_get(this.state.editIdentity, 'attributes.company.0', '').length > 1));
  }

  renderEditIdentity() {
    if(!this.state.editIdentity) {
      return null;
    }
    const identity = this.state.editIdentity;
    return (
        <Form>
          <Form.Field>
            <label>E-Mail</label>
            <Input value={identity.email}
                   onChange={(_, data) => {
                     this.setState(prevState => {
                       return {
                         editIdentity: {...prevState.editIdentity,
                                    email: data.value,
                                    username: data.value
                                }
                       };
                     }, () => this.checkUserAlreadyExists())
                   }}
                   icon={this.state.userMailState === 'checking' ? <Loader active={true} />: undefined}
            />
            {this.state.userMailState === 'free' && <Message visible={true} success={true}>E-Mail Adresse ist frei</Message> }
            {this.state.userMailState === 'exists' && (<Message visible={true} error={true}>
              E-Mail Adresse ist bereits vergeben. <br/>
            </Message>) }
          </Form.Field>

          <Form.Field>
            <label>Firma</label>
            <Input value={_get(identity, 'attributes.company.0', '')}
                   onChange={(_, data) => {
                     this.setState(prevState => {
                       const newIdentity: Partial<UserIdentity> = {...prevState.editIdentity};
                       newIdentity.attributes = {
                         company: [
                             data.value
                         ]
                       };
                       return {
                         editIdentity: newIdentity
                       }
                     })
                   }}
            />
          </Form.Field>

          <Form.Field>
            <label>Vorname</label>
            <Input value={identity.firstName}
                   onChange={(_, data) => {
                     this.setState(prevState => {
                       return {
                         editIdentity: {...prevState.editIdentity, firstName: data.value}
                       };
                     })
                   }}/>
          </Form.Field>

          <Form.Field>
            <label>Nachname</label>
            <Input value={identity.lastName}
                   onChange={(_, data) => {
                     this.setState(prevState => {
                       return {
                         editIdentity: {...prevState.editIdentity, lastName: data.value}
                       };
                     })
                   }}/>
          </Form.Field>

          <Button onClick={() => this.cancel()}>Abbrechen</Button>
          <Button primary disabled={!this.canSave()} onClick={() => this.saveIdentity()}>Speichern</Button>

          {this.state.error && this.state.error !== '' && <Message visible={true} warning={true}>{this.state.error}</Message>}
        </Form>
    );
  }

  render() {
    return (
        <>
          <AnalyticsTracking page="user_management/new" />
          <div className={"ui container"}>
            <Header as='h1'>
              <Icon name="users" />
              <Header.Content>
                Benutzerverwaltung
                <Header.Subheader>
                  Neuer Benutzer
                </Header.Subheader>
              </Header.Content>
            </Header>

            <Grid columns="2" stackable>
              <Grid.Column>
                <Header as='h2'>Identität</Header>
                {this.renderEditIdentity()}
              </Grid.Column>
              <Grid.Column>

              </Grid.Column>
            </Grid>

            <Dimmer active={this.state.saving} inverted>
              <Loader/>
            </Dimmer>

          </div>
        </>
    )
  }
}

export default withRouter(NewUser);